/**
 * Admin — amenity management: societies differ on which amenities exist
 * (some have a gym, some don't), their hours/slot length, rules text shown
 * to residents, and whether a slot is chargeable. List via the shared
 * ModerationScreen pattern (as vendors/notices/polls); edit via an inline
 * Sheet (name, hours, slot length, rules, paid toggle + fee); deactivate
 * asks for confirmation since it immediately hides the amenity from
 * residents (create uses its own screen — admin/create-amenity.tsx — same
 * convention as create-vendor.tsx).
 * Wired: GET/POST/PATCH /api/admin/amenities.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { formatINR, space } from '@bero/tokens';
import { bool, num, str, useGateList } from '@/components/gate/api';
import { resolvedAmenityIcon } from '@/components/money/bits';
import { Icon as AmenityGlyph } from '@/components/money/icons';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Chip, Field, Input, Pill, Sheet } from '@/ui';

type Row = {
  id: string; amenityId: number; name: string; openHour: number; closeHour: number;
  slotMinutes: number; rules: string; isPaid: boolean; feePaise: number; icon: string; active: boolean;
};

function rawStringArr(a: unknown, key: string): string[] {
  if (!a || typeof a !== 'object') return [];
  const v = (a as Record<string, unknown>)[key];
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

function toRow(a: unknown, i: number): Row {
  return {
    id: str(a, 'id') ?? `a${i}`,
    amenityId: num(a, 'id') ?? 0,
    name: str(a, 'name') ?? 'Amenity',
    openHour: num(a, 'openHour') ?? 6,
    closeHour: num(a, 'closeHour') ?? 22,
    slotMinutes: num(a, 'slotMinutes') ?? 120,
    rules: rawStringArr(a, 'rules').join('\n'),
    isPaid: bool(a, 'isPaid') ?? false,
    feePaise: num(a, 'feePaise') ?? 0,
    icon: str(a, 'icon') ?? '',
    active: bool(a, 'active', 'isActive') ?? true,
  };
}

type Form = {
  name: string; openHour: string; closeHour: string; slotMinutes: string;
  rules: string; isPaid: boolean; feePaise: string;
};

function formOf(r: Row): Form {
  return {
    name: r.name, openHour: String(r.openHour), closeHour: String(r.closeHour),
    slotMinutes: String(r.slotMinutes), rules: r.rules, isPaid: r.isPaid, feePaise: String(r.feePaise / 100),
  };
}

export default function AdminAmenitiesScreen() {
  const { colors } = useTheme();
  const { items, setItems, state } = useGateList<unknown>('/api/admin/amenities');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const [editing, setEditing] = React.useState<Row | null>(null);
  const [form, setForm] = React.useState<Form | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const [confirmDeactivate, setConfirmDeactivate] = React.useState<Row | null>(null);

  const openEdit = (row: Row) => {
    setFormError(null);
    setEditing(row);
    setForm(formOf(row));
  };

  const patchActive = (row: Row, isActive: boolean) => {
    const prev = items;
    setNotice(null);
    setItems(prev.map((a, i) => (toRow(a, i).id === row.id ? { ...(a as object), isActive } : a)));
    api.patch<unknown>(`/api/admin/amenities/${row.amenityId}`, { isActive }).then((body) => {
      if ((body as { needsApproval?: boolean })?.needsApproval) {
        setItems(prev); // not done yet — dual approval queued it
        setNotice('Sent for a second approval — track it under Pending actions.');
      }
    }).catch(() => {
      setItems(prev); // revert
      setNotice('Could not update the amenity. Try again.');
    });
  };

  const saveEdit = async () => {
    if (!editing || !form) return;
    const name = form.name.trim();
    if (!name) { setFormError('Name is required.'); return; }
    const openHour = Number(form.openHour);
    const closeHour = Number(form.closeHour);
    const slotMinutes = Number(form.slotMinutes);
    if (!Number.isFinite(openHour) || !Number.isFinite(closeHour) || !Number.isFinite(slotMinutes)) {
      setFormError('Hours and slot length must be numbers.');
      return;
    }
    const feePaise = form.isPaid ? Math.max(0, Math.round((Number(form.feePaise) || 0) * 100)) : 0;
    setSaving(true);
    setFormError(null);
    try {
      const body = await api.patch<{ amenity: unknown }>(`/api/admin/amenities/${editing.amenityId}`, {
        name, openHour, closeHour, slotMinutes, rules: form.rules, isPaid: form.isPaid, feePaise,
      });
      setItems((prev) => prev.map((a, i) => (toRow(a, i).id === editing.id ? body.amenity : a)));
      setEditing(null);
      setForm(null);
    } catch {
      setFormError('Could not save these changes. Please check the fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ModerationScreen
        title="Amenities"
        createLabel="Add amenity"
        onCreate={() => router.push('/admin/create-amenity')}
        state={state}
        count={rows.length}
        emptyIcon="users"
        emptyTitle="No amenities yet"
        emptyBody="Add a bookable amenity like a gym, pool or tennis court. Tap + to add the first one."
        notice={notice}
      >
        {rows.map((r) => (
          <Card key={r.id} style={!r.active ? { opacity: 0.6 } : undefined}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
              <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center' }}>
                <AmenityGlyph name={resolvedAmenityIcon(r)} size={18} color={colors.textSecondary} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                  <AppText variant="bodyMd" weight="500">{r.name}</AppText>
                  {!r.active ? (
                    <Pill status="rejected" label="OFF" />
                  ) : r.isPaid ? (
                    <Pill status="approved" label={`${formatINR(r.feePaise / 100)}/slot`} />
                  ) : null}
                </View>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                  {`${String(r.openHour).padStart(2, '0')}:00–${String(r.closeHour).padStart(2, '0')}:00 · ${r.slotMinutes}-min slots`}
                </AppText>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
              <SmallButton label="Edit" icon="edit" onPress={() => openEdit(r)} />
              <View style={{ flex: 1 }} />
              <SmallButton
                label={r.active ? 'Deactivate' : 'Reactivate'}
                danger={r.active}
                onPress={() => (r.active ? setConfirmDeactivate(r) : patchActive(r, true))}
              />
            </View>
          </Card>
        ))}
      </ModerationScreen>

      <Sheet visible={!!editing} onClose={() => { setEditing(null); setForm(null); }}>
        {editing && form ? (
          <>
            <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>Edit {editing.name}</AppText>
            <View style={{ gap: space.s4 }}>
              <Field label="Name">
                <Input value={form.name} onChangeText={(v) => setForm((f) => (f ? { ...f, name: v } : f))} />
              </Field>
              <View style={{ flexDirection: 'row', gap: space.s3 }}>
                <View style={{ flex: 1 }}>
                  <Field label="Opens (24h)">
                    <Input value={form.openHour} onChangeText={(v) => setForm((f) => (f ? { ...f, openHour: v } : f))} keyboardType="number-pad" />
                  </Field>
                </View>
                <View style={{ flex: 1 }}>
                  <Field label="Closes (24h)">
                    <Input value={form.closeHour} onChangeText={(v) => setForm((f) => (f ? { ...f, closeHour: v } : f))} keyboardType="number-pad" />
                  </Field>
                </View>
              </View>
              <Field label="Slot length (minutes)">
                <Input value={form.slotMinutes} onChangeText={(v) => setForm((f) => (f ? { ...f, slotMinutes: v } : f))} keyboardType="number-pad" />
              </Field>
              <Field label="Rules" help="One rule per line — shown to residents under the slot grid.">
                <Input
                  value={form.rules}
                  onChangeText={(v) => setForm((f) => (f ? { ...f, rules: v } : f))}
                  multiline
                  style={{ minHeight: 96, textAlignVertical: 'top' }}
                  placeholder={'Maximum 1 booking per day per flat\nCancel at least 2 hours before your slot'}
                />
              </Field>
              <Field label="Charges">
                <View style={{ flexDirection: 'row', gap: space.s2 }}>
                  <Chip label="Free" active={!form.isPaid} onPress={() => setForm((f) => (f ? { ...f, isPaid: false } : f))} />
                  <Chip label="Paid" active={form.isPaid} onPress={() => setForm((f) => (f ? { ...f, isPaid: true } : f))} />
                </View>
              </Field>
              {form.isPaid ? (
                <Field
                  label="Fee per slot (₹)"
                  help={Number(form.feePaise) > 0 ? `Residents see ${formatINR(Number(form.feePaise) || 0)} per slot` : 'e.g. 200'}
                >
                  <Input
                    value={form.feePaise}
                    onChangeText={(v) => setForm((f) => (f ? { ...f, feePaise: v } : f))}
                    keyboardType="number-pad"
                    placeholder="200"
                  />
                </Field>
              ) : null}
            </View>
            {formError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{formError}</AppText> : null}
            <Button title="Save changes" size="lg" loading={saving} onPress={() => void saveEdit()} style={{ marginTop: space.s5 }} />
            <Button title="Cancel" kind="tertiary" onPress={() => { setEditing(null); setForm(null); }} style={{ marginTop: 4 }} />
          </>
        ) : null}
      </Sheet>

      <Sheet visible={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        {confirmDeactivate ? (
          <>
            <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s3 }}>
              Deactivate {confirmDeactivate.name}?
            </AppText>
            <AppText variant="bodyMd" tone="secondary">
              Residents will no longer see or be able to book {confirmDeactivate.name} until you reactivate
              it. Existing bookings are not affected.
            </AppText>
            <Button
              title="Deactivate"
              kind="destructive"
              size="lg"
              onPress={() => { patchActive(confirmDeactivate, false); setConfirmDeactivate(null); }}
              style={{ marginTop: space.s5 }}
            />
            <Button title="Cancel" kind="tertiary" onPress={() => setConfirmDeactivate(null)} style={{ marginTop: 4 }} />
          </>
        ) : null}
      </Sheet>
    </>
  );
}
